import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldValues, Path, UseFormSetValue } from 'react-hook-form';

import { ActionModal } from 'components/mui/common/ActionModal';
import type { ModalsType } from 'types';
import { FileEditModalContent } from '../components/FileEditModalContent';
import { useFileEditModal } from '../hooks/useFileEditModal';
import type { FileUploadValue } from '../utils/toFileIds';

export type { FileUploadFormValue } from '../utils/toFileIds';

interface FileEditModalProps<K extends FieldValues> extends ModalsType {
  setValue: UseFormSetValue<K>;
  fieldName: Path<K>;
  value?: FileUploadValue;
  maxFilesNumber?: number | string | null;
}

export const FileEditModal = memo(<K extends FieldValues>({ open, onClose, setValue, fieldName, value, maxFilesNumber }: FileEditModalProps<K>) => {
  const { t } = useTranslation();
  const {
    filesData,
    uploadError,
    isUploading,
    isDisableUpload,
    showLoader,
    inputRef,
    getRootProps,
    getInputProps,
    handleRemoveFile,
    handleClose,
    handleSubmit,
    handleButtonOpenUpload,
    handleUploadFile,
  } = useFileEditModal({ open, onClose, setValue, fieldName, value, maxFilesNumber });

  return (
    <ActionModal
      maxWidth="md"
      open={open}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleSubmit}
      isSubmitting={isUploading}
      isOnSubmitDisabled={isUploading}
      title={t('common.files')}>
      <FileEditModalContent
        files={filesData}
        showLoader={showLoader}
        isUploading={isUploading}
        isDisableUpload={isDisableUpload}
        uploadError={uploadError}
        inputRef={inputRef}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        onOpenUpload={handleButtonOpenUpload}
        onUploadFile={handleUploadFile}
        onRemoveFile={handleRemoveFile}
      />
    </ActionModal>
  );
}) as <K extends FieldValues>(props: FileEditModalProps<K>) => JSX.Element;
